// v3.15-1134 — Payroll Reconciliation Dashboard
//
// Admin-only side-by-side comparison of the new Earnings Ledger vs the
// existing Payroll system. Per-recipient: Ledger Total | Payroll Total |
// Difference. Click any row to drill into every contributing row from
// BOTH systems so you can see exactly which entry causes the gap.
// Read-only. Does NOT modify either source of truth.

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, ChevronDown, ChevronRight, Loader2, ArrowLeft, FileSearch, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';

const fmtMoney = (n, opts = {}) => {
  const v = Number(n || 0);
  const s = v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  return opts.signed && v > 0 ? `+${s}` : s;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso.length === 10 ? iso + 'T00:00:00' : iso)
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const Tile = ({ label, amount, accent = 'blue', subtitle, testId }) => (
  <div
    data-testid={testId}
    className={`rounded-2xl bg-white p-5 shadow-sm border-2 ${
      accent === 'red'     ? 'border-rose-200'   :
      accent === 'green'   ? 'border-emerald-200':
      accent === 'amber'   ? 'border-amber-200'  :
                              'border-slate-200'
    }`}
  >
    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
    <div className={`text-3xl font-extrabold mt-1 ${
      accent === 'red'   ? 'text-rose-700'    :
      accent === 'green' ? 'text-emerald-700' :
      accent === 'amber' ? 'text-amber-700'   :
                            'text-slate-800'
    }`}>{fmtMoney(amount)}</div>
    {subtitle ? <div className="text-[11px] text-slate-500 mt-1">{subtitle}</div> : null}
  </div>
);

export default function ReconciliationPage() {
  const navigate = useNavigate();
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillId, setDrillId] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [onlyMismatches, setOnlyMismatches] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (year)  params.year = year;
      if (month) params.month = month;
      const res = await api.get('/earnings/reconciliation', { params });
      setData(res.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reconciliation failed to load');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const openDrill = async (row) => {
    setDrillId(row.user_id);
    setDrill(null);
    setDrillLoading(true);
    try {
      const params = {};
      if (year)  params.year = year;
      if (month) params.month = month;
      const res = await api.get(`/earnings/reconciliation/${row.user_id}`, { params });
      setDrill(res.data);
    } catch (e) {
      toast.error('Drill-down failed');
    } finally {
      setDrillLoading(false);
    }
  };

  const closeDrill = () => { setDrillId(null); setDrill(null); };

  const summary = data?.summary || {};
  const rows    = data?.rows || [];
  const visible = useMemo(() => onlyMismatches ? rows.filter((r) => !r.matches) : rows, [rows, onlyMismatches]);

  // Year options — show the year column as a fixed dropdown
  const yearOpts = (() => {
    const now = new Date().getFullYear();
    return ['', String(now), String(now - 1), String(now - 2)];
  })();

  return (
    <div className="space-y-4" data-testid="reconciliation-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/earnings')} data-testid="back-to-earnings">
          <ArrowLeft className="h-4 w-4 mr-1" /> My Earnings
        </Button>
        <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center">
          <Scale className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Payroll Reconciliation</h1>
          <p className="text-xs text-slate-500">v3.15-1134 · Ledger ↔ Payroll comparison · Read-only</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex flex-wrap items-center gap-3">
        <Select value={year || 'all'} onValueChange={(v) => { setYear(v === 'all' ? '' : v); setMonth(''); }}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="recon-year-filter"><SelectValue placeholder="All Time" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            {yearOpts.filter(Boolean).map((y) => (<SelectItem key={y} value={y}>{y}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={month || 'all'} onValueChange={(v) => setMonth(v === 'all' ? '' : v)} disabled={!year}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="recon-month-filter"><SelectValue placeholder="All Months" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Months</SelectItem>
            {Array.from({ length: 12 }).map((_, i) => (
              <SelectItem key={i + 1} value={String(i + 1)}>
                {new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 ml-2 text-xs text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={onlyMismatches}
            onChange={(e) => setOnlyMismatches(e.target.checked)}
            data-testid="only-mismatches-toggle"
          />
          Show only people with a difference
        </label>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile label="Ledger Total"  amount={summary.ledger_total}  accent="blue"  subtitle={`${summary.people || 0} people`} testId="tile-ledger" />
        <Tile label="Payroll Total" amount={summary.payroll_total} accent="amber" subtitle="Existing system" testId="tile-payroll" />
        <Tile
          label="Difference"
          amount={summary.difference}
          accent={(summary.difference || 0) === 0 ? 'green' : 'red'}
          subtitle={(summary.difference || 0) === 0 ? 'Perfect match' : 'Ledger − Payroll'}
          testId="tile-difference"
        />
        <Tile
          label="Mismatched"
          amount={summary.mismatched || 0}
          accent={summary.mismatched ? 'red' : 'green'}
          subtitle={`${summary.matching || 0} matching`}
          testId="tile-mismatched"
        />
      </div>

      {/* Per-person table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-testid="recon-table">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <div className="text-sm font-bold text-slate-900">By Recipient — Ledger vs Payroll</div>
          <div className="text-xs text-slate-500">{visible.length} of {rows.length} {rows.length === 1 ? 'person' : 'people'}</div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400 text-xs">
            <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Loading reconciliation…
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-10 text-emerald-600 text-sm font-medium" data-testid="recon-all-match">
            <CheckCircle2 className="h-6 w-6 mx-auto mb-1" />
            {rows.length === 0 ? 'No earnings or payroll rows in window.' : 'Everyone matches! No discrepancies.'}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-50">
              <div className="col-span-4">Person</div>
              <div className="col-span-2 text-right">Ledger</div>
              <div className="col-span-2 text-right">Payroll</div>
              <div className="col-span-2 text-right">Difference</div>
              <div className="col-span-2 text-center">Drill</div>
            </div>
            {visible.map((row) => (
              <div
                key={row.user_id}
                className={`grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-slate-50 ${
                  row.matches ? '' : Math.abs(row.difference) > 1000 ? 'bg-rose-50/30' : 'bg-amber-50/30'
                }`}
                data-testid={`recon-row-${row.user_id}`}
              >
                <div className="col-span-4 flex items-center gap-2 min-w-0">
                  {row.matches ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                  ) : (
                    <AlertCircle className={`h-4 w-4 flex-shrink-0 ${Math.abs(row.difference) > 1000 ? 'text-rose-500' : 'text-amber-500'}`} />
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{row.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono truncate">{row.user_id}</div>
                  </div>
                </div>
                <div className="col-span-2 text-right">
                  <div className="text-sm font-bold text-slate-900">{fmtMoney(row.ledger_total)}</div>
                  <div className="text-[10px] text-slate-500">{row.ledger_count} entries</div>
                </div>
                <div className="col-span-2 text-right">
                  <div className="text-sm font-bold text-slate-900">{fmtMoney(row.payroll_total)}</div>
                  <div className="text-[10px] text-slate-500">{row.payroll_count} entries</div>
                </div>
                <div className={`col-span-2 text-right text-sm font-bold ${
                  row.matches ? 'text-slate-400' :
                  row.difference > 0 ? 'text-emerald-700' : 'text-rose-700'
                }`}>
                  {fmtMoney(row.difference, { signed: true })}
                </div>
                <div className="col-span-2 text-center">
                  <Button
                    variant="outline" size="sm" onClick={() => openDrill(row)}
                    data-testid={`drill-${row.user_id}`}
                  >
                    <FileSearch className="h-3.5 w-3.5 mr-1" /> Drill
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Drill-down panel */}
      {drillId && (
        <div className="bg-white rounded-2xl shadow-md border-2 border-blue-200 overflow-hidden" data-testid="drill-panel">
          <div className="px-5 py-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-blue-900">
                Drill-down — {drill?.user?.full_name || 'Loading…'}
              </div>
              <div className="text-[11px] text-blue-700">
                Ledger {fmtMoney(drill?.ledger?.total)} ({drill?.ledger?.count || 0}) ·
                Payroll {fmtMoney(drill?.payroll?.total)} ({drill?.payroll?.count || 0}) ·
                Diff {fmtMoney((drill?.ledger?.total || 0) - (drill?.payroll?.total || 0), { signed: true })}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={closeDrill} data-testid="drill-close">
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
          {drillLoading ? (
            <div className="py-10 text-center text-slate-400 text-xs">
              <Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Loading drill-down…
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">
              {/* Ledger side */}
              <div className="p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Earnings Ledger ({drill?.ledger?.count || 0})
                </div>
                {(drill?.ledger?.rows || []).length === 0 ? (
                  <div className="text-xs text-slate-400 italic">No ledger entries in window.</div>
                ) : (
                  <div className="space-y-1 max-h-[420px] overflow-y-auto">
                    {(drill?.ledger?.rows || []).map((r) => (
                      <div key={r.id} className="flex items-center gap-2 text-xs py-1.5 border-b border-slate-100" data-testid={`drill-ledger-row-${r.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{r.category_label}</div>
                          <div className="text-[10px] text-slate-500 truncate">
                            {fmtDate(r.earned_date)} · {r.client_name || '—'} · {r.source}
                            {r.system_source ? ` (${r.system_source})` : ''}
                            {r.backfilled ? ' · BACKFILL' : ''}
                          </div>
                        </div>
                        <div className="font-bold text-slate-900">{fmtMoney(r.amount)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Payroll side */}
              <div className="p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Payroll Commissions ({drill?.payroll?.count || 0})
                </div>
                {(drill?.payroll?.rows || []).length === 0 ? (
                  <div className="text-xs text-slate-400 italic">No payroll entries in window.</div>
                ) : (
                  <div className="space-y-1 max-h-[420px] overflow-y-auto">
                    {(drill?.payroll?.rows || []).map((r) => (
                      <div key={r.id} className="flex items-center gap-2 text-xs py-1.5 border-b border-slate-100" data-testid={`drill-payroll-row-${r.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{r.commission_type || 'commission'}</div>
                          <div className="text-[10px] text-slate-500 truncate">
                            {fmtDate(r.trigger_date || r.payroll_date)} · {r.deal_source || ''} · status: {r.status || '—'}
                          </div>
                        </div>
                        <div className="font-bold text-slate-900">{fmtMoney(r.amount)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
